import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, User, Brain, KeyRound, Building2, MapPin, Clock, Trash2, Plus, Lock, Unlock,
  LocateFixed, Home, Briefcase, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { aviApi } from "@/lib/aviApi";
import { BrandMark } from "@/components/BrandMark";
import { cn } from "@/lib/utils";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function AviSettings() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [memories, setMemories] = useState([]);
  const [secretUnlocked, setSecretUnlocked] = useState(false);
  const [newMem, setNewMem] = useState({ text: "", profile: "shared", secret: false });
  const [pin, setPin] = useState("");
  const [verifyPinVal, setVerifyPinVal] = useState("");
  const [gviCode, setGviCode] = useState("");
  const [pins, setPins] = useState({ home_pin: null, work_pin: null });
  const [schedule, setSchedule] = useState({ workdays: [0, 1, 2, 3, 4], start: "09:00", end: "17:00", enabled: false });

  const load = useCallback(async () => {
    try {
      const r = await aviApi.get("/auth/me");
      setMe(r.data);
      setPins({ home_pin: r.data.prefs.home_pin, work_pin: r.data.prefs.work_pin });
      if (r.data.prefs.schedule) setSchedule({ ...r.data.prefs.schedule, enabled: true });
      const m = await aviApi.get("/me/memories");
      setMemories(m.data.memories || []);
      setSecretUnlocked(m.data.secret_unlocked);
    } catch (e) {
      navigate("/welcome");
    }
  }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const savePref = async (patch, msg = "Saved.") => {
    try {
      const r = await aviApi.put("/me/prefs", patch);
      setMe(r.data);
      toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
  };

  const addMemory = async () => {
    if (newMem.text.trim().length < 3) { toast.error("Tell Avi a little more."); return; }
    try {
      await aviApi.post("/me/memories", newMem);
      setNewMem({ text: "", profile: "shared", secret: false });
      toast.success("Remembered.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save memory");
    }
  };

  const deleteMemory = async (id) => {
    try {
      await aviApi.delete(`/me/memories/${id}`);
      setMemories((prev) => prev.filter((m) => m.id !== id));
      toast.success("Forgotten.");
    } catch (e) { toast.error("Couldn't delete"); }
  };

  const setPinNow = async () => {
    try {
      await aviApi.post("/me/pin", { pin });
      setPin("");
      toast.success("PIN saved.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "PIN must be 4-8 digits"); }
  };

  const verifyPinNow = async () => {
    try {
      const r = await aviApi.post("/me/pin/verify", { pin: verifyPinVal });
      setVerifyPinVal("");
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Incorrect PIN"); }
  };

  const linkGvi = async () => {
    try {
      const r = await aviApi.post("/auth/gvi-link", { gvi_code: gviCode });
      setGviCode("");
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid GVI code"); }
  };

  const unlinkGvi = async () => {
    try {
      await aviApi.delete("/auth/gvi-link");
      toast.success("Enterprise link removed.");
      load();
    } catch (e) { toast.error("Couldn't unlink"); }
  };

  const captureMyLocation = (key) => {
    if (!navigator.geolocation) { toast.error("Location not available in this browser."); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const pin = { lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6), radius_m: pins[key]?.radius_m || 150 };
        setPins((p) => ({ ...p, [key]: pin }));
        toast.success("Location captured — remember to save.");
      },
      () => toast.error("Couldn't get your location."),
      { timeout: 8000 }
    );
  };

  const saveLocations = () => {
    const patch = {};
    if (pins.home_pin) patch.home_pin = pins.home_pin;
    if (pins.work_pin) patch.work_pin = pins.work_pin;
    if (schedule.enabled) patch.schedule = { workdays: schedule.workdays, start: schedule.start, end: schedule.end };
    savePref(patch, "Locations & schedule saved. Avi will switch profiles automatically.");
  };

  const pinField = (key, label, Icon) => (
    <div className="rounded-xl border border-border p-3">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-medium"><Icon className="h-3.5 w-3.5 text-primary" /> {label}</span>
        <Button size="sm" variant="secondary" onClick={() => captureMyLocation(key)} data-testid={`use-location-${key}`}>
          <LocateFixed className="h-3.5 w-3.5" /> Use my location
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2.5">
        {["lat", "lng", "radius_m"].map((f) => (
          <div key={f}>
            <Label className="text-[10px] text-muted-foreground uppercase">{f === "radius_m" ? "Radius (m)" : f}</Label>
            <Input
              type="number"
              value={pins[key]?.[f] ?? ""}
              onChange={(e) => setPins((p) => ({ ...p, [key]: { ...(p[key] || { radius_m: 150 }), [f]: parseFloat(e.target.value) } }))}
              data-testid={`${key}-${f}-input`}
              className="mt-0.5 h-9"
            />
          </div>
        ))}
      </div>
    </div>
  );

  if (!me) return <div className="consumer min-h-screen avi-warmth" />;
  const prefs = me.prefs;

  return (
    <div className="consumer min-h-screen avi-warmth pb-16" data-testid="avi-settings-page">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center gap-3">
          <Link to="/app">
            <Button variant="ghost" size="icon" aria-label="Back" data-testid="settings-back-button">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <BrandMark className="h-8 w-8" />
          <div className="font-display font-semibold text-[15px]">Settings</div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 pt-6 space-y-6">
        {me.is_super_admin && (
          <Card className="p-5 rounded-2xl border-primary/30 bg-primary/5" data-testid="contextual-intelligence-card">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-primary/10 grid place-items-center shrink-0">
                <ShieldCheck className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h2 className="font-display text-sm font-semibold" data-testid="contextual-intelligence-title">Contextual Intelligence active</h2>
                <p className="text-xs text-muted-foreground mt-1" data-testid="contextual-intelligence-scope">
                  Founder access mirrors your communication style and can query every sanitized workspace module in Work mode. Personal memories remain isolated.
                </p>
              </div>
            </div>
          </Card>
        )}
        {/* Profile & tone */}
        <Card className="p-5 rounded-2xl" data-testid="settings-profile-card">
          <h2 className="font-display text-sm font-semibold flex items-center gap-2"><User className="h-4 w-4 text-primary" /> Profile & Tone</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="text-xs">What should Avi call you?</Label>
              <Input
                defaultValue={prefs.display_name}
                onBlur={(e) => e.target.value.trim() && e.target.value !== prefs.display_name && savePref({ display_name: e.target.value.trim() })}
                disabled={me.is_super_admin}
                data-testid="display-name-input" className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Tone</Label>
              <Select value={prefs.tone} onValueChange={(v) => savePref({ tone: v })}>
                <SelectTrigger className="mt-1" data-testid="tone-select"><SelectValue /></SelectTrigger>
                <SelectContent className="consumer">
                  <SelectItem value="warm">Warm & friendly</SelectItem>
                  <SelectItem value="direct">Direct & efficient</SelectItem>
                  <SelectItem value="playful">Playful</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reply length</Label>
              <Select value={prefs.response_length} onValueChange={(v) => savePref({ response_length: v })}>
                <SelectTrigger className="mt-1" data-testid="length-select"><SelectValue /></SelectTrigger>
                <SelectContent className="consumer">
                  <SelectItem value="short">Short & sweet</SelectItem>
                  <SelectItem value="balanced">Balanced</SelectItem>
                  <SelectItem value="detailed">Detailed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            Avi also learns your style automatically as you chat — these set the baseline.
          </p>
        </Card>

        {/* Memories & PIN */}
        <Card className="p-5 rounded-2xl" data-testid="settings-memory-card">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-sm font-semibold flex items-center gap-2"><Brain className="h-4 w-4 text-primary" /> Memories</h2>
            <Badge variant="outline" className="text-[10px]">{memories.length} kept</Badge>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Input
              value={newMem.text}
              onChange={(e) => setNewMem((m) => ({ ...m, text: e.target.value }))}
              placeholder="e.g. I'm allergic to shellfish"
              data-testid="new-memory-input"
            />
            <div className="flex items-center gap-2">
              <Select value={newMem.profile} onValueChange={(v) => setNewMem((m) => ({ ...m, profile: v, secret: v === "personal" ? m.secret : false }))}>
                <SelectTrigger className="w-[110px]" data-testid="new-memory-profile"><SelectValue /></SelectTrigger>
                <SelectContent className="consumer">
                  <SelectItem value="shared">Shared</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="work">Work</SelectItem>
                </SelectContent>
              </Select>
              {newMem.profile === "personal" && (
                <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Switch checked={newMem.secret} onCheckedChange={(v) => setNewMem((m) => ({ ...m, secret: v }))} data-testid="new-memory-secret" />
                  Secret
                </label>
              )}
              <Button size="icon" onClick={addMemory} data-testid="add-memory-button" aria-label="Add memory">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="mt-4 space-y-1.5 max-h-64 overflow-y-auto scrollbar-thin pr-1" data-testid="memories-list">
            {memories.map((m) => (
              <div key={m.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
                <Badge variant="outline" className={cn("text-[9px] capitalize shrink-0",
                  m.profile === "work" ? "border-blue-400/40 text-blue-600" :
                  m.profile === "personal" ? "border-amber-400/50 text-amber-700" : "border-primary/40 text-primary")}>
                  {m.profile}
                </Badge>
                {m.secret && <Lock className="h-3 w-3 text-amber-600 shrink-0" />}
                <span className={cn("text-xs flex-1 truncate", m.locked && "italic text-muted-foreground")}>{m.text}</span>
                <button onClick={() => deleteMemory(m.id)} data-testid={`delete-memory-${m.id}`} aria-label="Forget"
                  className="text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {memories.length === 0 && <p className="text-xs text-muted-foreground">Nothing yet — chat with Avi or add one above.</p>}
          </div>

          <div className="mt-5 rounded-xl border border-border bg-secondary/40 p-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <KeyRound className="h-3.5 w-3.5 text-primary" /> Secret memory PIN
              {prefs.pin_set && <Badge variant="outline" className="text-[9px]">set</Badge>}
              {secretUnlocked && <Badge variant="outline" className="text-[9px] border-primary/40 text-primary"><Unlock className="h-2.5 w-2.5 mr-0.5" /> unlocked</Badge>}
            </div>
            <div className="grid sm:grid-cols-2 gap-2 mt-2.5">
              <div className="flex gap-2">
                <Input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder={prefs.pin_set ? "New PIN (4-8 digits)" : "Set PIN (4-8 digits)"} data-testid="pin-set-input" />
                <Button variant="secondary" onClick={setPinNow} data-testid="pin-set-button">Save</Button>
              </div>
              <div className="flex gap-2">
                <Input type="password" inputMode="numeric" value={verifyPinVal} onChange={(e) => setVerifyPinVal(e.target.value)} placeholder="Enter PIN to unlock" data-testid="pin-verify-input" />
                <Button variant="secondary" onClick={verifyPinNow} disabled={!prefs.pin_set} data-testid="pin-verify-button">Unlock</Button>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Secret memories only surface in Personal chats after you unlock with your PIN (15 min).</p>
          </div>
        </Card>

        {/* Enterprise link */}
        <Card className="p-5 rounded-2xl" data-testid="settings-gvi-card">
          <h2 className="font-display text-sm font-semibold flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Workspace (GVI) Link</h2>
          {me.enterprise_linked ? (
            <div className="mt-3 flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5">
              <div className="text-sm">
                Linked to <span className="font-medium">{me.enterprise_name}</span>
                <div className="text-[11px] text-muted-foreground">Workspace knowledge appears in your Work profile only.</div>
              </div>
              <Button size="sm" variant="secondary" onClick={unlinkGvi} data-testid="gvi-unlink-button">Unlink</Button>
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              <Input value={gviCode} onChange={(e) => setGviCode(e.target.value)} placeholder="Enter your GVI governance code" data-testid="settings-gvi-input" />
              <Button onClick={linkGvi} disabled={!gviCode.trim()} data-testid="settings-gvi-link-button">Link</Button>
            </div>
          )}
        </Card>

        {/* Locations & schedule */}
        <Card className="p-5 rounded-2xl" data-testid="settings-context-card">
          <h2 className="font-display text-sm font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Auto-Switching (Locations & Schedule)</h2>
          <p className="text-[11px] text-muted-foreground mt-1">
            Avi flips between Work and Personal automatically — crossing your workplace perimeter or entering work hours.
          </p>
          <div className="space-y-3 mt-4">
            {pinField("work_pin", "Workplace pin", Briefcase)}
            {pinField("home_pin", "Home pin", Home)}
            <div className="rounded-xl border border-border p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-sm font-medium"><Clock className="h-3.5 w-3.5 text-primary" /> Work-hours schedule</span>
                <Switch checked={schedule.enabled} onCheckedChange={(v) => setSchedule((s) => ({ ...s, enabled: v }))} data-testid="schedule-enabled-switch" />
              </div>
              {schedule.enabled && (
                <div className="mt-3">
                  <div className="flex flex-wrap gap-1.5">
                    {DAYS.map((d, i) => (
                      <button
                        key={d}
                        onClick={() => setSchedule((s) => ({
                          ...s,
                          workdays: s.workdays.includes(i) ? s.workdays.filter((x) => x !== i) : [...s.workdays, i],
                        }))}
                        data-testid={`workday-${i}`}
                        className={cn("rounded-full border px-2.5 py-1 text-[11px] transition-colors",
                          schedule.workdays.includes(i) ? "border-primary/50 bg-primary/10 text-primary" : "border-border text-muted-foreground")}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2.5 max-w-[240px]">
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase">Start</Label>
                      <Input type="time" value={schedule.start} onChange={(e) => setSchedule((s) => ({ ...s, start: e.target.value }))} data-testid="schedule-start-input" className="mt-0.5 h-9" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase">End</Label>
                      <Input type="time" value={schedule.end} onChange={(e) => setSchedule((s) => ({ ...s, end: e.target.value }))} data-testid="schedule-end-input" className="mt-0.5 h-9" />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Button className="w-full" onClick={saveLocations} data-testid="save-context-button">Save auto-switching rules</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
